/**
 * The money view: headline tiles, net profit by month (one series, so the
 * title names it and there is no legend; a loss month is marked by colour),
 * and the same figures as tables.
 */
import { useState } from "react";
import { MonthBars } from "../components/MonthBars.tsx";
import { ErrorBlock, Loading } from "../components/ui.tsx";
import { t } from "../i18n/index.ts";
import { money, monthLabel, percent, todayIn } from "../lib/format.ts";
import { useFinance, useMarketplaces } from "../lib/queries.ts";
import { useWs } from "../lib/workspace.tsx";

type Range = "thisMonth" | "lastMonth" | "ytd" | "last12";
const RANGES: Range[] = ["thisMonth", "lastMonth", "ytd", "last12"];

function rangeDates(range: Range, today: string): { from: string; to: string } {
  const [y, m] = today.split("-").map(Number) as [number, number];
  const iso = (yy: number, mm: number, dd: number) => new Date(Date.UTC(yy, mm - 1, dd)).toISOString().slice(0, 10);
  switch (range) {
    case "thisMonth": return { from: iso(y, m, 1), to: today };
    case "lastMonth": return { from: iso(y, m - 1, 1), to: iso(y, m, 0) };
    case "ytd": return { from: iso(y, 1, 1), to: today };
    case "last12": return { from: iso(y, m - 11, 1), to: today };
  }
}

export function Finance() {
  const ws = useWs();
  const mps = useMarketplaces();
  const [range, setRange] = useState<Range>("thisMonth");
  const [personal, setPersonal] = useState(false);
  const { from, to } = rangeDates(range, todayIn(ws.timezone));
  const q = useFinance(ws.id, from, to, personal);
  const d = q.data;
  const c = ws.homeCurrency;

  return (
    <div className="stack-lg">
      <div className="page-head"><h1>{t("finance.title")}</h1></div>
      <div className="toolbar-row">
        <div className="chips" role="group">
          {RANGES.map((r) => (
            <button key={r} className="chip" aria-pressed={range === r} onClick={() => setRange(r)}>{t(`finance.${r}`)}</button>
          ))}
        </div>
        {ws.personalItems && (
          <label className="check small">
            <input type="checkbox" checked={personal} onChange={(e) => setPersonal(e.target.checked)} />
            {t("finance.includePersonal")}
          </label>
        )}
      </div>
      {q.isPending ? <Loading /> : q.isError ? <ErrorBlock error={q.error} onRetry={() => void q.refetch()} /> : d && (
        <>
          <div className="tile-row">
            <div className="tile"><div className="label">{t("finance.revenue")}</div><div className="value">{money(d.grossRevenueMinor, c)}</div></div>
            <div className="tile">
              <div className="label">{t("finance.net")}</div>
              <div className={`value ${d.netProfitMinor >= 0 ? "pos" : "neg"}`}>{money(d.netProfitMinor, c)}</div>
              {d.grossRevenueMinor > 0 && <div className="foot">{t("finance.margin")} {percent(d.netProfitMinor / d.grossRevenueMinor)}</div>}
            </div>
            <div className="tile"><div className="label">{t("finance.units")}</div><div className="value">{d.unitsSold}</div></div>
            <div className="tile">
              <div className="label">{t("finance.capital")}</div>
              <div className="value">{money(d.inventory.capitalAtRestMinor, c)}</div>
              <div className="foot">{t("finance.activeUnits")} {d.inventory.activeUnits} · {t("finance.listedUnits")} {d.inventory.listedUnits}</div>
            </div>
          </div>

          <section className="card">
            <h2>{t("finance.net")} · {t("finance.byMonth")}</h2>
            {d.months.length === 0 ? <p className="muted small">{t("finance.noSales")}</p> : (
              <>
                {d.months.length > 1 && <MonthBars data={d} />}
                <div className="table-wrap">
                  <table className="table">
                    <thead>
                      <tr><th>{t("finance.month")}</th><th className="r">{t("finance.units")}</th><th className="r">{t("finance.revenue")}</th><th className="r">{t("finance.net")}</th></tr>
                    </thead>
                    <tbody>
                      {d.months.map((m) => (
                        <tr key={m.month}>
                          <td>{monthLabel(m.month)}</td>
                          <td className="r">{m.unitsSold}</td>
                          <td className="r">{money(m.grossRevenueMinor, c)}</td>
                          <td className="r">{money(m.netProfitMinor, c)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </section>

          <div className="grid-2">
            <section className="card">
              <h2>{t("finance.byMarketplace")}</h2>
              {d.byMarketplace.length === 0 ? <p className="muted small">{t("finance.noSales")}</p> : (
                <table className="table">
                  <tbody>
                    {d.byMarketplace.map((m) => (
                      <tr key={m.marketplace}>
                        <td>{mps.data?.find((x) => x.code === m.marketplace)?.name ?? m.marketplace}</td>
                        <td className="r">{m.unitsSold}</td>
                        <td className="r">{money(m.grossRevenueMinor, c)}</td>
                        <td className="r">{money(m.netProfitMinor, c)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>
            <section className="card">
              <h2>{t("finance.net")}</h2>
              <dl className="kv">
                <dt>{t("finance.revenue")}</dt><dd>{money(d.grossRevenueMinor, c)}</dd>
                <dt>{t("finance.fees")}</dt><dd>−{money(d.feesMinor, c)}</dd>
                <dt>{t("finance.cogs")}</dt><dd>−{money(d.costOfGoodsMinor, c)}</dd>
                <dt>{t("finance.otherCosts")}</dt><dd>−{money(d.otherCostsMinor, c)}</dd>
                <dt className="total">{t("finance.net")}</dt><dd className="total">{money(d.netProfitMinor, c)}</dd>
              </dl>
            </section>
          </div>
        </>
      )}
    </div>
  );
}
